import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAgency } from '../lib/AgencyContext';
import { useI18n } from '../lib/i18n';
import Icon from './Icon';

const initialOf = (name) => (String(name || '?').trim()[0] || '?').toUpperCase();

// Da li je ekran dovoljno širok za stalno vidljiv meni (skupljanje važi samo tada)
function useIsDesktop() {
  const q = '(min-width: 1024px)';
  const [ok, setOk] = useState(() => window.matchMedia(q).matches);
  useEffect(() => {
    const m = window.matchMedia(q);
    const fn = (e) => setOk(e.matches);
    m.addEventListener ? m.addEventListener('change', fn) : m.addListener(fn);
    return () => { m.removeEventListener ? m.removeEventListener('change', fn) : m.removeListener(fn); };
  }, []);
  return ok;
}

export default function Sidebar({ open, onClose, collapsed = false, onToggleCollapse }) {
  const { profile, activeAgency, activeAgencyId, exitAgency, year, setYear, years, currentYear } = useAgency();
  const location = useLocation();
  const { t, tr } = useI18n();
  const isDesktop = useIsDesktop();
  const compact = collapsed && isDesktop;
  const toggleLabel = compact
    ? tr('Proširi meni')
    : tr('Skupi meni');

  // Broj faktura koje čekaju naplatu (poslato ili delimično plaćeno) — osvežava se pri svakoj promeni stranice
  const [unpaid, setUnpaid] = useState(0);
  useEffect(() => {
    if (!activeAgencyId) { setUnpaid(0); return; }
    let cancelled = false;
    supabase
      .from('invoices')
      .select('id', { count: 'exact', head: true })
      .eq('agency_id', activeAgencyId)
      .neq('document_kind', 'profaktura')
      .in('status', ['poslato', 'delimicno'])
      .then(({ count }) => { if (!cancelled) setUnpaid(count || 0); });
    return () => { cancelled = true; };
  }, [activeAgencyId, location.pathname]);
  const unpaidTitle = tr('Čeka naplatu', 'Awaiting payment', 'Ожидает оплаты');

  // Meni je grupisan po poslu: Prodaja (sve oko izdavanja računa) i Porezi i evidencija (sve što ide državi).
  // Podešavanja (profil, agencija, notifikacije, preporuke, odjava) su u meniju ispod imena gore desno.
  const NAV_SECTIONS = [
    {
      label: null,
      items: [
        { to: '/', icon: 'home', label: t('navHome'), end: true },
      ],
    },
    {
      label: tr('Prodaja', 'Sales', 'Продажи'),
      items: [
        {
          to: '/fakturisanje', icon: 'receipt', label: t('invoicing'), count: unpaid,
          children: [
            { to: '/fakturisanje?vrsta=faktura', label: tr('Fakture', 'Invoices', 'Счета') },
            { to: '/fakturisanje?vrsta=profaktura', label: tr('Profakture', 'Proformas', 'Проформы') },
            { to: '/avansi', label: tr('Avansi', 'Advances', 'Авансы'), badge: tr('Uskoro', 'Soon', 'Скоро') },
            { to: '/efakture', label: tr('eFakture', 'e-Invoices', 'Э-счета') },
          ],
        },
        { to: '/pazar', icon: 'chart', label: tr('Dnevni pazar', 'Daily takings', 'Дневная выручка') },
        { to: '/fiskalna-kasa', icon: 'calculator', label: t('navFiscalCash'), badge: tr('Uskoro', 'Soon', 'Скоро') },
        { to: '/klijenti', icon: 'users', label: t('clients') },
        { to: '/proizvodi', icon: 'package', label: t('navProducts') },
      ],
    },
    {
      label: tr('Porezi i evidencija', 'Taxes & records', 'Налоги и учёт'),
      items: [
        { to: '/porezi', icon: 'percent', label: t('taxes') },
        { to: '/kpo-knjiga', icon: 'book', label: t('navKpoBook') },
        { to: '/poreski-kalendar', icon: 'calendarDay', label: t('navTaxCalendar') },
        { to: '/kursna-lista', icon: 'exchange', label: t('navExchangeList') },
      ],
    },
  ];

  const supportLabel = tr('Pomoć i podrška', 'Help & support', 'Помощь и поддержка');

  // Podmeni Fakturisanja: otvoren po difoltu; aktivna stavka po putanji i ?vrsta=
  const loc = useLocation();
  const [subOpen, setSubOpen] = useState(true);
  const isSubActive = (to) => {
    const [path, q] = to.split('?');
    if (!q) return loc.pathname === path || loc.pathname.startsWith(`${path}/`);
    if (loc.pathname !== path) return false;
    return new URLSearchParams(loc.search).get('vrsta') === new URLSearchParams(q).get('vrsta');
  };

  const navStyle = ({ isActive }) => ({
    ...styles.navItem,
    ...(compact ? styles.navItemCompact : {}),
    ...(isActive ? styles.navItemActive : {}),
  });

  const accountName = activeAgency?.name || profile?.company_name || t('yourAgencyDefault');

  return (
    <>
      {open && <div style={styles.backdrop} onClick={onClose} className="no-print sidebar-backdrop" />}
      <aside
        style={{
          ...styles.sidebar,
          ...(compact ? styles.sidebarCompact : {}),
          transform: open ? 'translateX(0)' : 'translateX(-100%)',
        }}
        className="no-print app-sidebar"
      >
        <div style={{ ...styles.brandRow, ...(compact ? styles.brandRowCompact : {}) }}>
          <div style={styles.logoBadge}>FinArt<br />Studio</div>
          {!compact && <span style={styles.brandText}>FinArt <span style={{ fontWeight: 400 }}>Studio</span></span>}
          {isDesktop && (
            <button
              onClick={onToggleCollapse}
              style={{ ...styles.collapseBtn, ...(compact ? { marginLeft: 0 } : {}) }}
              title={toggleLabel}
              aria-label={toggleLabel}
            >
              <Icon name="panelLeft" size={17} />
            </button>
          )}
          <button onClick={onClose} style={styles.closeBtn} className="sidebar-close-btn" aria-label={t('closeMenuAria')}>
            <Icon name="x" size={16} />
          </button>
        </div>

        <button
          onClick={exitAgency}
          style={{ ...styles.companyRow, ...(compact ? styles.companyRowCompact : {}) }}
          title={compact ? `${accountName} · ${t('switchAccount')}` : t('switchAccount')}
        >
          <span style={styles.companyAvatar}>{initialOf(accountName)}</span>
          {!compact && (
            <>
              <span style={styles.companyText}>
                <span style={styles.companyName}>{accountName}</span>
                <span style={styles.companySub}>{t('switchAccount')}</span>
              </span>
              <Icon name="chevronsUpDown" size={16} color="var(--ink-500)" />
            </>
          )}
        </button>

        {/* Izbor godine — uvek kreće od tekuće; ranije godine su u listi */}
        <label
          style={{ ...styles.yearRow, ...(compact ? styles.yearRowCompact : {}), ...(year !== currentYear ? styles.yearRowPast : {}) }}
          title={t('yearLabel')}
        >
          {!compact && (
            <span style={styles.yearLabel}>
              <Icon name="calendar" size={16} />
              {t('yearLabel')}
            </span>
          )}
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} style={styles.yearSelect}>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </label>
        {year !== currentYear && !compact && (
          <button onClick={() => setYear(currentYear)} style={styles.backToCurrent}>
            {t('backToCurrentYear', currentYear)}
          </button>
        )}

        <nav style={styles.nav}>
          {NAV_SECTIONS.map((section, si) => (
            <div key={section.label || `s${si}`} style={styles.section}>
              {section.label && (compact
                ? <div style={styles.sectionDivider} />
                : <div style={styles.sectionLabel} className="mono-label">{section.label}</div>)}
              {section.items.map((item) => (
                <div key={item.to}>
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={onClose}
                  title={compact ? item.label : undefined}
                  aria-label={compact ? item.label : undefined}
                  style={navStyle}
                >
                  <Icon name={item.icon} size={compact ? 20 : 18} />
                  {!compact && <span style={styles.navLabel}>{item.label}</span>}
                  {!compact && item.badge && <span style={styles.badge}>{item.badge}</span>}
                  {!compact && item.count > 0 && <span style={styles.countBadge} title={unpaidTitle}>{item.count}</span>}
                  {compact && (item.badge || item.count > 0) && <span style={styles.badgeDot} />}
                  {!compact && item.children && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setSubOpen((o) => !o); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setSubOpen((o) => !o); } }}
                      style={styles.chev}
                      aria-label={subOpen ? 'Skupi' : 'Raširi'}
                    >
                      <Icon name={subOpen ? 'chevronUp' : 'chevronDown'} size={14} />
                    </span>
                  )}
                </NavLink>
                {/* Podmeni (Fakture, Profakture, Avansi, eFakture) */}
                {!compact && item.children && subOpen && (
                  <div style={styles.subList}>
                    {item.children.map((c) => {
                      const active = isSubActive(c.to);
                      return (
                        <NavLink key={c.to} to={c.to} onClick={onClose} style={{ ...styles.subItem, ...(active ? styles.subItemActive : {}) }} aria-current={active ? 'page' : undefined}>
                          <span>{c.label}</span>
                          {c.badge && <span style={styles.badge}>{c.badge}</span>}
                        </NavLink>
                      );
                    })}
                  </div>
                )}
                </div>
              ))}
            </div>
          ))}
        </nav>

        {/* Dole: pomoć i podrška */}
        <div style={{ ...styles.bottomArea, ...(compact ? styles.bottomAreaCompact : {}) }}>
          <NavLink
            to="/podrska"
            onClick={onClose}
            title={compact ? supportLabel : undefined}
            aria-label={compact ? supportLabel : undefined}
            style={navStyle}
          >
            <Icon name="headset" size={compact ? 20 : 18} />
            {!compact && <span style={styles.navLabel}>{supportLabel}</span>}
          </NavLink>
        </div>
      </aside>

      <style>{`
        @media (min-width: 1024px) {
          .app-sidebar { transform: translateX(0) !important; }
          .sidebar-backdrop { display: none !important; }
          .sidebar-close-btn { display: none !important; }
        }
        .app-sidebar a:hover { background: rgba(255,255,255,0.7); }
        .app-sidebar a[aria-current="page"]:hover { background: var(--surface); }
        @media (min-width: 1024px) {
          .app-sidebar { top: 12px !important; left: 12px !important; bottom: 12px !important; border: 1px solid var(--line) !important; border-radius: 16px; }
        }
        .app-sidebar a[aria-current="page"] svg { stroke: var(--lavender-700); stroke-width: 2; }
        /* Bez debelog okvira posle klika mišem; okvir ostaje samo za kretanje tastaturom (Tab) */
        .app-sidebar a:focus:not(:focus-visible),
        .app-sidebar button:focus:not(:focus-visible) { outline: none; box-shadow: none; }
        .app-sidebar a:focus-visible, .app-sidebar button:focus-visible { outline: 2px solid var(--lavender-700); outline-offset: 1px; }
      `}</style>
    </>
  );
}

const styles = {
  backdrop: {
    position: 'fixed', inset: 0, background: 'rgba(12,37,48,0.4)', zIndex: 30,
  },
  sidebar: {
    // Verde: meni je zasebna "ploča" koja lebdi (na računaru odmaknuta 12px od ivica)
    position: 'fixed', top: 0, left: 0, bottom: 0, width: 240, background: '#f8fafc',
    borderRight: '1px solid var(--line)', zIndex: 31, display: 'flex', flexDirection: 'column',
    // Logo, firma i godina stoje u mestu — skroluje se samo spisak stranica (nav)
    padding: '20px 12px', overflow: 'hidden', transition: 'transform 0.25s ease, width 0.2s ease',
  },
  sidebarCompact: { width: 60, padding: '20px 6px', alignItems: 'center' },
  brandRowCompact: { flexDirection: 'column', padding: '0 0 16px', gap: 12 },
  collapseBtn: {
    marginLeft: 'auto', border: '1px solid var(--line)', background: 'var(--surface)', borderRadius: 10,
    width: 34, height: 34, color: 'var(--ink-500)', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  companyRowCompact: { width: 48, height: 48, padding: 0, justifyContent: 'center' },
  yearRowCompact: { padding: '2px 4px', justifyContent: 'center', width: 52 },
  sectionDivider: { width: 32, height: 1, background: 'var(--line)', margin: '4px auto 10px' },
  navItemCompact: { justifyContent: 'center', padding: 0, width: 48, height: 44, position: 'relative' },
  badgeDot: { position: 'absolute', top: 9, right: 10, width: 7, height: 7, borderRadius: '50%', background: 'var(--gold-500)' },
  brandRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '0 8px 18px' },
  closeBtn: {
    marginLeft: 'auto', border: '1px solid var(--line)', background: 'var(--surface)', borderRadius: 10,
    width: 36, height: 36, color: 'var(--teal-800)', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  logoBadge: {
    width: 32, height: 32, borderRadius: '50%', background: 'var(--teal-800)',
    color: 'var(--lavender-300)', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center',
    fontWeight: 800, fontSize: 6.5, lineHeight: 1.05, flexShrink: 0,
  },
  brandText: { fontWeight: 700, fontSize: 18, color: 'var(--ink-900)', letterSpacing: '-1px', whiteSpace: 'nowrap' },
  companyRow: {
    display: 'flex', alignItems: 'center', gap: 10, background: 'var(--surface)', border: '1px solid var(--line)',
    borderRadius: 10, padding: '9px 10px', marginBottom: 8, width: '100%', textAlign: 'left',
  },
  companyAvatar: {
    width: 28, height: 28, borderRadius: 8, background: 'var(--teal-800)', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0,
  },
  companyText: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' },
  companyName: { fontSize: 13, fontWeight: 700, color: 'var(--ink-900)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  companySub: { fontSize: 11, color: 'var(--ink-500)' },
  yearRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
    borderRadius: 10, padding: '2px 6px 2px 12px', marginBottom: 6,
  },
  yearRowPast: { borderColor: 'var(--gold-300)', background: 'var(--gold-100)' },
  yearLabel: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 500, color: 'var(--ink-500)' },
  yearSelect: {
    border: 'none', background: 'transparent', fontSize: 14, fontWeight: 700, color: 'var(--teal-800)',
    padding: '4px 2px', cursor: 'pointer',
  },
  backToCurrent: {
    border: 'none', background: 'none', color: 'var(--teal-700)', fontSize: 12, fontWeight: 600,
    padding: '0 4px', marginBottom: 6, textAlign: 'left', cursor: 'pointer',
  },
  nav: { flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'thin', marginTop: 14 },
  section: { marginBottom: 16 },
  sectionLabel: {
    fontSize: 11, fontWeight: 700, color: 'var(--ink-500)', textTransform: 'uppercase',
    letterSpacing: '0.08em', padding: '0 12px', marginBottom: 6,
  },
  navItem: {
    display: 'flex', alignItems: 'center', gap: 11, padding: '8px 11px', borderRadius: 9,
    fontSize: 13, fontWeight: 500, color: 'var(--ink-700)', textDecoration: 'none', marginBottom: 2,
  },
  // Izabrana stavka: bela "pločica" sa tankom senkom (Verde)
  chev: { marginLeft: 6, width: 22, height: 22, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-500)' },
  subList: { display: 'flex', flexDirection: 'column', gap: 2, margin: '2px 0 6px 20px', paddingLeft: 10, borderLeft: '1px solid var(--line)' },
  subItem: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '6px 10px', borderRadius: 8, fontSize: 12.5, fontWeight: 500, color: 'var(--ink-700)', textDecoration: 'none' },
  subItemActive: { background: 'var(--surface)', color: 'var(--teal-800)', fontWeight: 600, boxShadow: '0 1px 2px rgba(18,52,63,0.06), 0 0 0 1px var(--line)' },
  navItemActive: { background: 'var(--surface)', color: 'var(--teal-800)', fontWeight: 600, boxShadow: '0 1px 2px rgba(18,52,63,0.06), 0 0 0 1px var(--line)' },
  navLabel: { flex: 1 },
  badge: {
    fontSize: 10, fontWeight: 700, background: 'var(--gold-100)', color: 'var(--gold-600)',
    borderRadius: 6, padding: '2px 7px',
  },
  countBadge: {
    fontSize: 11, fontWeight: 700, background: 'var(--teal-100)', color: 'var(--teal-800)',
    borderRadius: 999, minWidth: 22, padding: '2px 7px', textAlign: 'center',
  },
  bottomArea: { borderTop: '1px solid var(--line)', paddingTop: 10, marginTop: 8 },
  bottomAreaCompact: { width: 48, display: 'flex', justifyContent: 'center' },
};
